import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled, { useTheme } from "styled-components";
import { formatDateTimeFromEpoch } from "../../utils/helpers";
import { OrderedCellRendererProps } from "../Grid/types";
import { getGridClickBoundaryProps } from "../Grid/GridClickBoundary";
import { ColoredTooltip } from "../shared/Tooltip/ColoredTooltip";
import { EditOrderTooltipText } from "./NewReqNameAndActionsCell.styles";

const ORDER_PLACED_DATE_FONT_SIZE = 14;
const ORDER_PLACED_DATE_LINE_HEIGHT = ORDER_PLACED_DATE_FONT_SIZE;
const EDITED_INDICATOR_TOP_GAP = 8;
const EDITED_INDICATOR_BOTTOM_GAP = 8;
const EDITED_INDICATOR_LINE_HEIGHT = 12;

const OrderPlacedWrapper = styled.div<{ $hasEditedIndicator: boolean }>`
  align-items: flex-start;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${({ $hasEditedIndicator }) =>
    $hasEditedIndicator ? `${EDITED_INDICATOR_TOP_GAP}px` : 0};
  height: 100%;
  justify-content: ${({ $hasEditedIndicator }) =>
    $hasEditedIndicator ? "flex-end" : "center"};
  min-width: 0;
  padding-bottom: ${({ $hasEditedIndicator }) =>
    $hasEditedIndicator ? `${EDITED_INDICATOR_BOTTOM_GAP}px` : 0};
`;

const OrderPlacedDate = styled.span`
  color: ${({ theme }) => theme.greyMedium};
  font-size: ${ORDER_PLACED_DATE_FONT_SIZE}px;
  line-height: ${ORDER_PLACED_DATE_LINE_HEIGHT}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EditedIndicator = styled.span<{ $color: string }>`
  align-items: center;
  color: ${({ $color }) => $color};
  display: inline-flex;
  flex-shrink: 0;
  gap: 4px;
  line-height: ${EDITED_INDICATOR_LINE_HEIGHT}px;

  svg {
    font-size: 9px;
  }
`;

const EditedText = styled.span`
  font-family: light;
  font-size: 12px;
  line-height: ${EDITED_INDICATOR_LINE_HEIGHT}px;
`;

const formatEditedAt = (lastEditedAt: string): string => {
  const formattedDate = formatDateTimeFromEpoch(lastEditedAt) || lastEditedAt;
  return formattedDate.replace(", ", " at ");
};

export const UpdatedBadge: React.FC<{
  updatedAt: string;
  updatedBy?: string | null;
  label?: string;
  color: string;
}> = ({ updatedAt, updatedBy, label = "Updated on", color }) => {
  const theme = useTheme();
  return (
    <ColoredTooltip
      arrow
      placement="right"
      backgroundColor={theme.greyDark}
      title={
        <EditOrderTooltipText>
          {label} {formatEditedAt(updatedAt)}
          {updatedBy && (
            <>
              <br />
              by {updatedBy}
            </>
          )}
        </EditOrderTooltipText>
      }
    >
      <EditedIndicator
        aria-label="updated"
        $color={color}
        {...getGridClickBoundaryProps()}
      >
        <FontAwesomeIcon icon={faPenToSquare} />
        <EditedText>updated</EditedText>
      </EditedIndicator>
    </ColoredTooltip>
  );
};

export const NewReqOrderPlacedCell: React.FC<OrderedCellRendererProps> = ({
  data,
}) => {
  const theme = useTheme();
  const hasEditedIndicator = Boolean(data.last_edited_at);

  return (
    <OrderPlacedWrapper $hasEditedIndicator={hasEditedIndicator}>
      <OrderPlacedDate>
        {formatDateTimeFromEpoch(data.created_at)}
      </OrderPlacedDate>
      {data.last_edited_at && (
        <UpdatedBadge
          updatedAt={data.last_edited_at}
          updatedBy={data.last_edited_by}
          color={theme.redMedium}
        />
      )}
    </OrderPlacedWrapper>
  );
};
